//Lib
import { action, computed, observable, runInAction } from "mobx";
import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment, { Moment } from "moment";

//App
import { AppUrls } from "../../AppUrls";

// Stores
import { StoresInstance } from "../Stores";
import { ApplicationFormStatusStore, SaveStore } from "Globals/Stores/Domain";
const statusStore: ApplicationFormStatusStore = StoresInstance.Domain.ApplicationFormStatusStore;
const saveStore: SaveStore = StoresInstance.Domain.SaveStore;

// ViewModels
import StepViewModel from "./FormSections/StepViewModel";
import ApplicantViewModel from "./ApplicantViewModel";

//Models
import { InitialState } from "../Models";
import { ApplicantModel } from "Globals/Models/Domain/ApplicantModel";
import { ApplicationFormModel } from "../Models/Domain/ApplicationFormModel";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { ApplicationStepEnum } from "Globals/Models/ApplicationStepEnum";
import { ApplicationFormStatus, ApplicationFormStatusEnum } from "Globals/Models";

// Dto
import { ApplicantDTO } from "Globals/Models/DTO/ApplicantDTO";
import { ApplicationFormAndRelatedDTO } from "Globals/Models/DTO/ApplicationFormAndRelatedDTO";
import { ApplicationFormDTO } from "Globals/Models/DTO/ApplicationFormDTO";
import { UpsertApplicationFormDTO } from "Globals/Models/DTO/Upsert/UpsertApplicationFormDTO";
import { AdminDecisionEnum } from "Globals/Models/AdminDecisionEnum";
const domainStores = StoresInstance.Domain;

interface UpdateApplicationSupportingInfoDTO {
    applicationFormId: string;
}

interface AdminDecisionDTO {
    formId: string;
    decision: AdminDecisionEnum;
    rejectReasonId: string | null;
    rejectReasonDetail: string | null;
}

interface UpdateApplicationStatusDTO {
    formId: string;
    applicationStatus: ApplicationFormStatusEnum;
}

export default class ApplicationFormViewModel extends ViewModelBase<ApplicationFormModel> {
    @observable public stepViewModel: StepViewModel = new StepViewModel();
    @observable public applicantViewModels: ApplicantViewModel[] = [];
    @observable public showProceedForStep: ApplicationStepEnum = ApplicationStepEnum.Step1;

    constructor(id: string | undefined) {
        super(new ApplicationFormModel());
        this.loadApplicationFormAndRelatedByIdAsync(id);
    }

    @action
    public async applicationDecision(decision: AdminDecisionEnum, reason: string | null, reasonDetail: string | null) {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let adminDecision: AdminDecisionDTO = {
            formId: this.model.id,
            decision: decision,
            rejectReasonId: reason,
            rejectReasonDetail: reasonDetail,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.ApproveAmendReject, adminDecision);
        if (apiResult.wasSuccessful) {
            domainStores.OpenSnackBar("Decision sent", "success");
        } else {
            domainStores.OpenSnackBar(`Error: ${apiResult.errors}`, "error");
        }
        this.setIsLoading(false);
    }

    @action
    public async updateApplicationFormStatus(applicationStatus: ApplicationFormStatusEnum): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let payload: UpdateApplicationStatusDTO = {
            formId: this.model.id,
            applicationStatus: applicationStatus,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.UpdateApplicationStatus, payload);
        if (apiResult.wasSuccessful) {
            domainStores.OpenSnackBar("Done", "success");

            //generate pdf if payment has been received
            if (applicationStatus == ApplicationFormStatusEnum.ReservationPaid) {
                this.generateApplicantFormPdf();
            }
        } else {
            domainStores.OpenSnackBar(`Error`, "error");
        }

        this.setIsLoading(false);
    }

    @action
    public async updateApplicationSupportingInfo(): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let payload: UpdateApplicationSupportingInfoDTO = {
            applicationFormId: this.model.id,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.UpdateApplicationSupportingInfo, payload);
        if (apiResult.wasSuccessful) {
            domainStores.OpenSnackBar("Done", "success");
        } else {
            domainStores.OpenSnackBar(`Error`, "error");
        }

        this.setIsLoading(false);
    }

    public refresh() {
        if (this.model.id) {
            this.clearDataForReload();
            this.loadApplicationFormAndRelatedByIdAsync(this.model.id);
        }
    }

    @computed
    public get getSortedApplicantViewModels(): ApplicantViewModel[] {
        return this.applicantViewModels.slice().sort((a, b) => a.model.applicantNumber - b.model.applicantNumber);
    }

    @computed
    public get applicationStatusEnum(): ApplicationFormStatusEnum {
        let applicationStatus = statusStore.getStatus(this.model.statusId);
        if (applicationStatus) {
            return applicationStatus.type;
        }
        return ApplicationFormStatusEnum.AmendmentsRequired;
    }

    @computed
    public get status(): ApplicationFormStatus | undefined {
        return statusStore.getStatus(this.model.statusId);
    }
    @computed
    public get statusName(): string {
        return statusStore.getStatusName(this.model.statusId);
    }
    @computed
    public get statusIsLocked(): boolean {
        let lockedNames = ["Validated - Reservation paid", "Under review", "Awaiting Review", "Application validated payment required"];
        let name = statusStore.getStatusName(this.model.statusId);
        return lockedNames.includes(name);
    }
    @computed
    private get lastSavedMoment() {
        return moment.utc(this.model.lastSavedDate);
    }

    @computed
    public get lastSaved(): string {
        return this.lastSavedMoment ? this.lastSavedMoment.local().format("HH:mm - DD/MM/YYYY") : "";
    }

    @computed
    public get requestedSupportingInfo(): boolean {
        return this.model.requestedSupportingInfo;
    }

    // @action
    // public generateApplicantFormPdf = () => {
    //     const requestString = AppUrls.Server.ApplicationForm.GenerateApplicationFormPdfById + "/" + this.model.id;
    //     window.open(requestString, "_blank");
    // };

    @action
    public async loadApplicationFormAndRelatedByIdAsync(applicationId: string | undefined): Promise<ApiResult<ApplicationFormAndRelatedDTO>> {
        this.setIsLoading(true);
        const id = applicationId !== null && applicationId !== undefined && applicationId !== "" && applicationId !== ":applicationId" ? applicationId : null;
        const path = id === null ? AppUrls.Server.ApplicationForm.GetApplicationFormAndRelatedById : AppUrls.Server.ApplicationForm.GetApplicationFormAndRelatedById + "/?id=" + id;
        const apiResult = await this.Get<ApplicationFormAndRelatedDTO>(path);
        if (apiResult.wasSuccessful) {
            //Reset any data
            this.clearDataForReload();

            //Load new data
            this.model.fromDto(apiResult.payload);
            this.createViewModels();
        }
        this.setIsLoading(false);

        return apiResult;
    }

    @action
    private clearDataForReload(): void {
        this.model.resetModel();
        this.model.applicantModels = [];
        this.applicantViewModels = [];
    }

    @action
    private createViewModels(): void {
        for (const applicant of this.model.applicantModels) {
            this.applicantViewModels.push(new ApplicantViewModel(applicant));
        }
    }

    @computed
    public get numberOfApplicants(): number {
        return this.applicantViewModels.length;
    }

    @action
    public handleApplicantChange(applicantNumber: number): void {
        this.upsertApplicant(applicantNumber);
    }

    @action
    public async generateApplicantFormPdf(): Promise<void> {
        this.setIsLoading(true);

        const apiResult = await this.Post<ApplicantDTO>(AppUrls.Server.ApplicationForm.GenerateApplicationFormPdfById, this.model.id);

        if (apiResult.wasSuccessful) {
            console.log("Upserted applicant pdf", apiResult.payload);
            await this.loadApplicationFormAndRelatedByIdAsync(this.model.id ? this.model.id : "");
            this.setIsLoading(false);
        } else {
            console.error("Upserted applicant pdf", apiResult.errors);
            this.setIsLoading(false);
        }
    }

    @action
    public async upsertApplicant(applicantNumber: number): Promise<void> {
        this.setIsLoading(true);
        try {
            if (applicantNumber < this.numberOfApplicants) {
                //Delete applicants
                let applicants = this.model.applicantModels.filter((a) => a.applicantNumber > applicantNumber);
                applicants.forEach(async (applicant) => {
                    applicant.isDeleted = true;
                    await this.upsertApplicantDTO(applicant.toDto());
                    await this.loadApplicationFormAndRelatedByIdAsync(this.model.id ? this.model.id : "");
                });
            } else {
                // Create new applicants
                let numberOfApplicantsToAdd = applicantNumber - this.numberOfApplicants;
                for (let index = 0; index < numberOfApplicantsToAdd; index++) {
                    const applicantDto: ApplicantDTO = {
                        id: "",
                        formId: this.model.id ? this.model.id : "",
                        applicantNumber: applicantNumber,
                        primaryApplicant: false,
                        createdDate: null,
                        isDeleted: false,
                    };
                    await this.upsertApplicantDTO(applicantDto);
                    await this.loadApplicationFormAndRelatedByIdAsync(this.model.id ? this.model.id : "");
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            // Finally
            this.setIsLoading(false);
        }
    }

    @action
    private async upsertApplicantDTO(dto: ApplicantDTO): Promise<void> {
        const apiResult = await this.Post<ApplicantDTO>(AppUrls.Server.ApplicationForm.Upsert.Applicant, dto);
        if (apiResult.wasSuccessful) {
            console.log("Upserted applicant", apiResult.payload);
        } else {
            console.error("Upserted applicant", apiResult.errors);
        }
    }

    @action
    public save(): void {
        saveStore.saveAll();
    }
    @action
    public submit = async (): Promise<void> => {
        try {
            await saveStore.saveAll();
            this.setIsLoading(true);
            let isModelValid: boolean = await this.isModelValid();
            console.log("Application form model is valid: " + isModelValid);
            if (isModelValid) {
                let upsertApplicationForm: UpsertApplicationFormDTO = this.model.toDto();
                upsertApplicationForm.applicationForm.isSubmitted = true; // Set the application as submitted
                const apiResult = await this.Post<UpsertApplicationFormDTO>(AppUrls.Server.ApplicationForm.Upsert.Application, upsertApplicationForm);

                if (apiResult.wasSuccessful) {
                    console.info("Application form - upsert successful");
                    this.history.push(AppUrls.Client.ThankYouForSubmitting);
                } else {
                    console.error("Application form - upsert error", apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
            console.error("Application form - upsert exception", exception);
        } finally {
            // Finally
            this.setIsLoading(false);
        }
    };

    public async isFieldValid(fieldName: keyof FieldType<ApplicationFormModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
